import { useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

import { Constants } from "../../database.types"

const TEAMS = Constants.public.Enums.app_team

type Team = (typeof TEAMS)[number]

const TEAM_PERMISSIONS = Constants.public.Enums.team_permission

type TeamPermission = (typeof TEAM_PERMISSIONS)[number]

const prettyLabel = (perm: string) => perm.replace(/_/g, " ")
const extractScope = (perm: string) => perm.split(".").slice(1).join(".")

const ALL_SCOPES = Array.from(new Set(TEAM_PERMISSIONS.map(extractScope))).sort()

export default function TeamPermissions() {
  const [team, setTeam] = useState<Team | "">("")
  const [scope, setScope] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<Set<TeamPermission>>(new Set())
  const [pending, setPending] = useState<Set<TeamPermission>>(new Set())

  const scopePermissions: TeamPermission[] = useMemo(
    () => TEAM_PERMISSIONS.filter((p) => extractScope(p) === scope),
    [scope],
  )

  // Reset and fetch when team or scope changes
  useEffect(() => {
    setExisting(new Set())
    setError(null)
    if (!team || !scope) return

    const fetchExisting = async () => {
      setLoading(true)
      setError(null)
      const allowedForScope = TEAM_PERMISSIONS.filter((p) => extractScope(p) === scope)
      const { data, error: err } = await supabase
        .from("team_permissions")
        .select("permission")
        .eq("team", team)
        .in("permission", allowedForScope)

      if (err) {
        console.error(err)
        setError(err.message)
        setLoading(false)
        return
      }

      const perms = new Set<TeamPermission>((data || []).map((d) => d.permission as TeamPermission))
      setExisting(perms)
      setLoading(false)
    }

    fetchExisting()
  }, [team, scope])

  const togglePermission = async (perm: TeamPermission) => {
    if (!team || !scope) return
    if (pending.has(perm)) return
    setError(null)
    setPending((prev) => new Set(prev).add(perm))
    try {
      if (existing.has(perm)) {
        const { error: dErr } = await supabase
          .from("team_permissions")
          .delete()
          .eq("team", team)
          .eq("permission", perm)
        if (dErr) throw dErr
        setExisting((prev) => {
          const next = new Set(prev)
          next.delete(perm)
          return next
        })
      } else {
        const { error: iErr } = await supabase
          .from("team_permissions")
          .insert({ team, permission: perm })
        if (iErr) throw iErr
        setExisting((prev) => {
          const next = new Set(prev)
          next.add(perm)
          return next
        })
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg || "Failed to update permission")
    } finally {
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(perm)
        return next
      })
    }
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader>
          <CardTitle>Team Permissions Editor</CardTitle>
          <CardDescription>
            Hardcoded teams and permissions. Uses browser Supabase client to read/write the{" "}
            <code>team_permissions</code> table. Choose a team and a scope from the left columns.
            Toggling a permission immediately inserts/deletes rows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Column 1: Teams */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Teams</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <ul className="space-y-2">
                  {TEAMS.map((t) => (
                    <li key={t}>
                      <Button
                        variant={team === t ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setTeam(t)}
                      >
                        {t}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Column 2: Scopes */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Scopes</CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <ul className="space-y-2">
                  {ALL_SCOPES.map((t) => (
                    <li key={t}>
                      <Button
                        variant={scope === t ? "default" : "outline"}
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setScope(t)}
                      >
                        {t}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Column 3: Permissions */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base">Permissions</CardTitle>
                <CardDescription>
                  {team && scope
                    ? loading
                      ? "Loading…"
                      : `${Array.from(existing).filter((p) => extractScope(p) === scope).length} enabled of ${scopePermissions.length}`
                    : "Select a team and a scope"}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-3">
                {team && scope ? (
                  loading ? (
                    <div className="text-sm text-muted-foreground">Loading current selections…</div>
                  ) : (
                    <ul className="grid grid-cols-1 gap-2">
                      {scopePermissions.map((perm) => (
                        <li key={perm} className="flex items-center gap-2">
                          <Checkbox
                            id={perm}
                            checked={existing.has(perm)}
                            disabled={pending.has(perm)}
                            onCheckedChange={() => togglePermission(perm)}
                          />
                          <Label htmlFor={perm} className="text-sm">
                            {prettyLabel(perm)}
                            {pending.has(perm) && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (updating…)
                              </span>
                            )}
                          </Label>
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  <div className="text-sm text-muted-foreground">Select a team and a scope</div>
                )}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}
